import { useContext, useMemo } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'

import { Alert, Button, Card, CardColors, Dialog, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { useAnalyticsTrack } from '@/features/amplitude'
import {
  DELETE_MANUAL_TRANSACTION_KEY,
  UPDATE_USER_TRANSACTION_KEY,
  deleteManualTransaction,
  updateUserTransaction,
} from './transactions.slice'

import {
  UndoTransactionContext,
  UNDO_TRANSACTION_TYPES,
} from './UndoTransactionContext'

interface TransactionDeleteModalProps {
  open: boolean
  close: () => void
  transactionId: number
  isManualTransaction: boolean
}
export const TransactionDeleteModal = ({
  open,
  close,
  transactionId,
  isManualTransaction,
}: TransactionDeleteModalProps) => {
  const dispatch = useAppDispatch()
  const deleteIsFetching = useReselector(
    getIsFetching,
    DELETE_MANUAL_TRANSACTION_KEY
  )
  const deleteError = useReselector(
    getFetchError,
    DELETE_MANUAL_TRANSACTION_KEY
  )

  const dupeIsFetching = useReselector(
    getIsFetching,
    UPDATE_USER_TRANSACTION_KEY
  )
  const dupeError = useReselector(getFetchError, UPDATE_USER_TRANSACTION_KEY)

  const { setUndoTransactionId, setUndoTransactionType } = useContext(
    UndoTransactionContext
  )

  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  const buttonOrder = useMemo(() => {
    return isMobile ? 1 : 0
  }, [isMobile])

  const content = useMemo(() => {
    return isManualTransaction
      ? {
          header: 'Are you sure you want to delete?',
          body: 'Deleting this transaction will:',
          button: 'Delete Transaction',
        }
      : {
          header: 'Are you sure you want to mark as duplicate?',
          body: 'Marking this transaction as a duplicate will:',
          button: 'Mark as Duplicate',
        }
  }, [isManualTransaction])

  const track = useAnalyticsTrack()

  const deleteTransaction = async () => {
    if (isManualTransaction) {
      track('deleted manual transaction')
      const deleted = await deleteManualTransaction(transactionId)(dispatch)
      if (deleted) {
        setUndoTransactionId(transactionId)
        setUndoTransactionType(UNDO_TRANSACTION_TYPES.DELETE)
        close()
      }
    } else {
      track('marked transaction as duplicate')
      const updated = await updateUserTransaction(transactionId, {
        manuallyDedupedAt: new Date().toISOString(),
      })(dispatch)
      if (updated) {
        setUndoTransactionId(transactionId)
        setUndoTransactionType(UNDO_TRANSACTION_TYPES.DUPLICATE)
        close()
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && close()}>
      <Dialog.Content className="max-w-sm sm:max-w-md">
        <Dialog.Title>{content.header}</Dialog.Title>

        <Dialog.Description asChild>
          <div className="flex flex-col gap-y-4 text-sm">
            <Text variant={TypographyStyles.BODY_SM}>{content.body}</Text>

            <Card color={CardColors.DANGER}>
              <Text variant={TypographyStyles.BODY_SM}>
                <b>Permanently remove</b> it from your Transactions, Profit &
                Loss, and reports.
              </Text>
            </Card>

            <Text variant={TypographyStyles.BODY_SM}>
              This cannot be undone.
            </Text>

            {dupeError && (
              <Alert
                variant="error"
                description="Error marking transaction as duplicate. Please try again."
              />
            )}

            {deleteError && (
              <Alert variant="error">
                <Text>Error deleting transaction. Please try again.</Text>
              </Alert>
            )}
          </div>
        </Dialog.Description>

        <Dialog.Footer
          className={
            isMobile
              ? 'flex flex-col gap-2 mt-6'
              : 'justify-end mt-6 flex flex-row gap-2'
          }
        >
          <Dialog.Close asChild>
            <Button
              variant="link"
              className={`no-underline ${buttonOrder === 1 ? 'order-1' : 'order-0'}`}
            >
              Cancel
            </Button>
          </Dialog.Close>

          <Button
            variant="danger"
            onClick={deleteTransaction}
            loading={dupeIsFetching || deleteIsFetching}
          >
            {content.button}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
